import React from "react";
import {OrderEditorTabProps} from "@widgets/pos/order/OrderEditor";
import {Alert, ButtonGroup, ButtonGroupButton, Icon} from "peggirkit";
import {BuildingStorefrontIcon, ShoppingBagIcon, TruckIcon} from "@heroicons/react/20/solid";
import {FormattedMessage} from "react-intl";
import {useFormContext, useWatch} from "react-hook-form";
import {PosOrderForm, REQUESTED_TIME_ASAP, REQUESTED_TIME_PRESENT} from "@/data/entities/order";
import {useFulfillmentType} from "@widgets/pos/order/hooks/UseFulfillmentType";
import {emptyNewDeliveryAddress} from "@/data/entities/deliveryAddress";
import {useExistingOrder} from "@widgets/pos/order/hooks/UseExistingOrder";

const FulfillmentOptions = ({changeTabRef, isPending}: OrderEditorTabProps) => {
    const {setValue, getValues} = useFormContext<PosOrderForm>();
    const fulfillmentType = useFulfillmentType();
    const requestedTime = useWatch<PosOrderForm, "requestedTime">({name: "requestedTime"});
    const isLocked: boolean = useExistingOrder() !== null;

    // Takeaway for a customer who's already at the counter
    const isPresent: boolean = fulfillmentType === "TAKEAWAY" && requestedTime === REQUESTED_TIME_PRESENT;

    const goToNextTab = () => {
        if (changeTabRef.current)
            changeTabRef.current(1);
    };

    // Leaving "customer is here": back to the defaults of a normal order. An
    // existing order keeps its payment.
    const leavePresent = () => {
        if (getValues("requestedTime") !== REQUESTED_TIME_PRESENT)
            return;

        setValue("requestedTime", REQUESTED_TIME_ASAP, {shouldDirty: true});
        if (!isLocked)
            setValue("paymentStatus", "PAY_LATER", {shouldDirty: true});
    };

    const selectTakeaway = () => {
        setValue("deliveryAddress", {type: "TAKEAWAY"}, {shouldDirty: true});
        leavePresent();
    };

    const selectPresent = () => {
        setValue("deliveryAddress", {type: "TAKEAWAY"}, {shouldDirty: true});
        setValue("requestedTime", REQUESTED_TIME_PRESENT, {shouldDirty: true});

        // A new order is paid at the counter right away (PIN)
        if (!isLocked)
            setValue("paymentStatus", "PAID", {shouldDirty: true});
    };

    const selectDelivery = () => {
        leavePresent();

        // Keep an address that's already filled in
        if (getValues("deliveryAddress.type") !== "TAKEAWAY")
            return;

        // Existing customer: start with the first address of the account
        const firstAddress = getValues("customer")?.deliveryAddresses[0];
        setValue(
            "deliveryAddress",
            firstAddress ? {type: "EXISTING", id: firstAddress.id} : emptyNewDeliveryAddress(),
            {shouldDirty: true},
        );
    };

    const option = (
        icon: typeof TruckIcon,
        messageId: string,
        selected: boolean,
        disabled: boolean,
        select: () => void,
    ) => (
        <ButtonGroupButton
            size={"lg"}
            variant={"outline"}
            iconBefore={<Icon icon={icon}/>}
            buttonAction={{
                as: "button",
                actionProps: {
                    onClick: (e) => {
                        e.preventDefault();
                        select();
                        goToNextTab();
                    },
                },
            }}
            selected={selected}
            disabled={isPending || disabled}
        >
            <FormattedMessage id={messageId}/>
        </ButtonGroupButton>
    );

    return (
        <div className={"w-full flex items-start justify-center p-4"}>
            <div className={"mt-16 w-full max-w-[600px] flex flex-col gap-6 items-center justify-start"}>
                {
                    isLocked
                        ? <Alert
                            type={"warning"}
                            title={<FormattedMessage id={"fulfillmentLocked"}/>}
                            content={<FormattedMessage id={"fulfillmentLockedDescription"}/>}
                        />
                        : <Alert
                            type={"primary"}
                            title={<FormattedMessage id={"bewareOfChangingFulfillment"}/>}
                            content={<FormattedMessage id={"bewareOfChangingFulfillmentDescription"}/>}
                        />
                }

                <ButtonGroup>
                    {option(
                        TruckIcon,
                        "delivery",
                        fulfillmentType === "DELIVERY",
                        isLocked && fulfillmentType !== "DELIVERY",
                        selectDelivery,
                    )}
                    {option(
                        ShoppingBagIcon,
                        "takeaway",
                        fulfillmentType === "TAKEAWAY" && !isPresent,
                        isLocked && fulfillmentType !== "TAKEAWAY",
                        selectTakeaway,
                    )}
                    {option(
                        BuildingStorefrontIcon,
                        "takeawayCustomerPresent",
                        isPresent,
                        isLocked && fulfillmentType !== "TAKEAWAY",
                        selectPresent,
                    )}
                </ButtonGroup>
            </div>
        </div>
    );
};

export default FulfillmentOptions;